import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  Check,
  X,
  ArrowRight,
  Compass,
  MessageCircle,
  Target,
  Sparkles,
  BookOpen,
  Users,
  CalendarCheck,
  HeartHandshake,
  Repeat,
  Instagram,
  Menu,
  Mail,
} from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOKING_LINK = "https://calendly.com/lydia-he/consistent-clients";
const INSTAGRAM_HANDLE = "@lydiakimmerling"; // replace with real handle
const INSTAGRAM_URL = "https://instagram.com/lydiakimmerling";

const HERO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/170mvlqu_Untitled%20design%20%2819%29.png";
const ABOUT_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/vdjxe1v3_Untitled%20design%20%2821%29.png";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/28xtkcxe_logo-1.png";
const LEAD_MAGNET_PDF =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/qasg33pd_The_Consistent_Clients_Guide%20%281%29.pdf";

const EVIDENCE = [
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/tfd3ik6r_Untitled%20design%20%2824%29.png",
    caption: "Coaching live",
    sub: "In the room with clients",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/paymjeke_Untitled%20design%20%2823%29.png",
    caption: "On stage",
    sub: "Keynote speaking",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/1gpg9tp8_Untitled%20design%20%2822%29.png",
    caption: "Live event",
    sub: "Speaking to a room",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/bou2z4gp_IMG_1801%20%281%29.jpg",
    caption: "With Fearne Cotton",
    sub: "Happy Place Festival",
  },
];

const PRESS = [
  {
    name: "TEDx",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/bmhcwz1y_tedx-1.png",
  },
  {
    name: "BBC",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/mi8oya18_bbc-logo-1.png",
  },
  {
    name: "Happy Place Festival",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/d5trnnjs_Happy-Place-Festival-Logo.png",
    brighten: true,
  },
  {
    name: "The Times",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/lsbalefj_The-Times.png",
  },
  {
    name: "Hello!",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/05zcuw3s_Hello.png",
  },
  {
    name: "Marie Claire",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/tc3us6oh_marie-claire-logo.png",
  },
  {
    name: "Metro",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/i80kxbvq_metro-logo.png",
  },
  {
    name: "Health & Fitness",
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/0egzj0m1_health-fitness.png",
  },
];

const INBOX_WINS = [
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/eimpyapm_WhatsApp%20Image%202021-03-24%20at%2010.01.57.jpg",
    caption: "Fully booked for the rest of the year",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/uzlc492e_IMG_3032%20%281%29.jpg",
    caption: "20 discovery calls booked",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/m9yt57d5_IMG_1188%20%281%29.jpg",
    caption: "International clients, from Sri Lanka to Berlin",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/hbrwiktd_IMG_1568%20%281%29.jpg",
    caption: "Her first paying client",
  },
];

const PAIN_POINTS = [
  "You've been showing up online, but clients aren't coming in consistently",
  "Feast and famine income that you just can't make reliable",
  "You've tried courses and strategies, and you're a little sceptical because none of it has stuck",
  "You want something steady, well-paid and manageable in real life",
  "You're tired of pushing and proving yourself for inconsistent results",
  "You need more than a course. You need someone actually in your corner",
];

const PILLARS = [
  {
    icon: Compass,
    title: "Clarify",
    body:
      "Pin down exactly who you serve, what you sell and the words that make people stop and pay attention. No more shouting into the void.",
  },
  {
    icon: MessageCircle,
    title: "Connect",
    body:
      "Build a steady rhythm of real conversations with the right people. Not more content. More contact. The kind that actually leads to clients.",
  },
  {
    icon: Target,
    title: "Convert",
    body:
      "Sell with integrity. Calls that feel honest, offers that feel right and a process that turns interest into paying, repeat clients.",
  },
  {
    icon: Repeat,
    title: "Continue",
    body:
      "Make consistency the norm, not the exception. Refine, repeat and retain, until £5k–£10k months feel ordinary, not lucky.",
  },
];

const INCLUDED = [
  { icon: Sparkles, title: "The Positioning Intensive", note: "Private 1:1 session" },
  { icon: Users, title: "30 Leads in 30 Days Sprint", note: "Done together, not alone" },
  { icon: CalendarCheck, title: "Twice-Weekly Mentoring Calls", note: "Client Flow strategy + coaching" },
  { icon: MessageCircle, title: "The Consistent Conversations Strategy", note: "Your daily method" },
  { icon: BookOpen, title: "The Steady Clients Training Library", note: "Watch anytime, return anytime" },
  { icon: HeartHandshake, title: "Personalised Strategy + Coaching", note: "Built around your real life" },
  { icon: Target, title: "Weekly Action + Accountability", note: "So nothing slips" },
  { icon: Repeat, title: "Ongoing Client Flow Refinement", note: "Stay with you until it's steady" },
];

const TESTIMONIALS = [
  {
    quote:
      "Lyd — I'm fully booked for the rest of the year. I can't take on any more clients.",
    name: "Meg",
  },
  {
    quote: "It has honestly been the best investment I've made in myself.",
    name: "Louise",
  },
  {
    quote:
      "Lydia really cares about my progress. You can't put a price on it. If only there were more Lydias out there!",
    name: "Kellie Anne",
  },
  { quote: "20 discovery calls booked!! That is scary!!", name: "Carolin" },
];

const NOTABLE = [
  {
    name: "Ferne McCann",
    title: "TV personality & model",
    quote:
      "Working with Lydia as my coach gave me the confidence to believe in the new business we were launching. She gave me the accountability to keep going whilst helping me trust and let go along the way.",
    image:
      "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/o962idye_WhatsApp-Image-2023-09-18-at-23.41.57.jpg",
    imagePos: "70% 30%",
  },
  {
    name: "Jeff Brazier",
    title: "TV presenter",
    quote:
      "Lydia is a big part of all that's coming good in my life. My work with her has been nothing short of transformational, helping me go from being a habitual believer to simply knowing.",
    image:
      "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/dvytfo6t_0954ba24-11ee-4e3f-b6b4-31f6c9428975%20%281%29.jpg",
    imagePos: "50% 35%",
  },
];

const FIT_YES = [
  "You run a service-based business (or you're building one)",
  "You're in years 1–3 and ready for steady income",
  "You're willing to show up, do the work and have real conversations",
  "You want personalised support, not just a course in a vault",
  "You're done with quick fixes and ready for something that lasts",
];

const FIT_NO = [
  "You're looking for a magic, overnight quick fix",
  "Your business relies on manifestation",
  "You only want a passive course with zero engagement",
  "You're not willing to send a message or have a sales conversation",
];

// ---------- Components ----------

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 bg-ivory/85 backdrop-blur-md border-b border-espresso/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="block leading-none"
        >
          <img
            src={LOGO_URL}
            alt="Lydia Kimmerling — Faith Over Force"
            className="h-12 md:h-14 w-auto"
            style={{ filter: "invert(1)", mixBlendMode: "multiply" }}
          />
        </a>

        <nav className="hidden md:flex items-center gap-9 text-sm font-sans text-espresso/80">
          <a href="#programme" className="link-underline" data-testid="nav-programme">The Programme</a>
          <a href="#included" className="link-underline" data-testid="nav-included">What's Included</a>
          <a href="#about" className="link-underline" data-testid="nav-about">About Lydia</a>
          <a href="#testimonials" className="link-underline" data-testid="nav-testimonials">Stories</a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://wa.me/message/42HORKSQ4JQNJ1"
            data-testid="nav-whatsapp-btn"
            className="btn-primary hidden sm:inline-flex"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="lg:hidden">Question? WhatsApp me</span>
            <span className="hidden lg:inline">Got a question? WhatsApp me</span>
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-espresso p-2"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
          >
            <Menu className="w-6 h-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-espresso/10 bg-ivory px-6 py-5 flex flex-col gap-4 text-espresso font-sans">
          <a href="#programme" onClick={() => setOpen(false)}>The Programme</a>
          <a href="#included" onClick={() => setOpen(false)}>What's Included</a>
          <a href="#about" onClick={() => setOpen(false)}>About Lydia</a>
          <a href="#testimonials" onClick={() => setOpen(false)}>Stories</a>
          <a href="https://wa.me/message/42HORKSQ4JQNJ1" className="btn-primary mt-2 w-full" onClick={() => setOpen(false)} target="_blank" rel="noopener noreferrer">
            Got a question? WhatsApp me
          </a>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="top" className="relative bg-ivory overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 pb-20 md:pt-24 md:pb-28 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7 order-2 lg:order-1">
        <Reveal>
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-6">
            Mentoring for Women in Service-Based Business
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1
            data-testid="hero-headline"
            className="font-serif text-espresso leading-[0.95] tracking-tight text-[3.4rem] sm:text-[4.2rem] md:text-[5.4rem] lg:text-[6.2rem]"
          >
            Faith <em className="italic font-medium">Over</em> Force.
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-8 font-sans text-lg md:text-xl text-espresso/80 max-w-xl leading-relaxed">
            A high-support mentoring programme for women in service-based businesses who want
            consistent clients, steadier income and a business that works without burning them out.
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-6 font-serif italic text-xl md:text-2xl text-espresso/90 max-w-xl leading-snug">
            Move from inconsistent £1k–£2k months to steady £5k–£10k months, with strategy, deep
            coaching and personalised support until consistent clients become your normal.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href={BOOKING_LINK} data-testid="hero-book-call-btn" className="btn-primary" target="_blank" rel="noopener noreferrer">
              Book a Free Consistent Clients Call
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <a href="#programme" className="btn-outline" data-testid="hero-learn-more-btn">
              Learn More
            </a>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-5 order-1 lg:order-2 relative">
        <Reveal delay={0.1}>
          <div className="relative">
            <img
              src={HERO_PORTRAIT}
              alt="Lydia Kimmerling portrait"
              data-testid="hero-portrait"
              className="relative w-full aspect-square object-cover"
              style={{ objectPosition: "center" }}
              loading="eager"
            />
          </div>
        </Reveal>
      </div>
    </div>

    {/* Press bar */}
    <div className="bg-espresso">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha mb-7 text-center md:text-left">
          As Seen On
        </p>
        <div
          data-testid="press-bar"
          className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 md:gap-x-8 lg:gap-x-12 gap-y-6"
        >
          {PRESS.map((p) =>
            p.type === "image" ? (
              <img
                key={p.name}
                src={p.src}
                alt={p.name}
                className="h-10 md:h-12 lg:h-14 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
                style={{
                  maxWidth: "180px",
                  filter: p.brighten ? "brightness(4) contrast(1.1)" : undefined,
                }}
              />
            ) : (
              <span
                key={p.name}
                className="font-serif italic text-ivory/70 hover:text-ivory transition-colors text-lg md:text-xl tracking-wide"
              >
                {p.name}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  </section>
);

const PainPoints = () => (
  <section id="pain" className="relative bg-mocha text-ivory grain">
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <Reveal>
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-ivory/70 mb-5">
          Is this you?
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
          You're good at what you do. You're just not getting paid <em className="italic">consistently</em> for it.
        </h2>
      </Reveal>

      <ul className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-6" data-testid="pain-list">
        {PAIN_POINTS.map((p, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <li className="flex gap-4 items-start py-3 border-b border-ivory/15">
              <span className="mt-1 shrink-0 w-7 h-7 rounded-full bg-ivory/10 border border-ivory/30 flex items-center justify-center">
                <Check className="w-4 h-4 text-ivory" strokeWidth={2} />
              </span>
              <span className="font-sans text-base md:text-lg leading-relaxed text-ivory/90">
                {p}
              </span>
            </li>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const RealProblem = () => (
  <section className="relative bg-espresso text-ivory">
    <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-36 lg:py-44 text-center">
      <Reveal>
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-6">
          The Real Problem
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-[2.5rem] md:text-5xl lg:text-[4rem] leading-[1.05]">
          More content isn't the answer. <br className="hidden md:block" />
          <em className="italic">More conversations</em> are.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-10 font-sans text-base md:text-lg text-ivory/75 max-w-3xl mx-auto leading-relaxed">
          The real issues usually aren't your offer or your work ethic. They're unclear
          positioning, not enough genuine conversations, no consistent strategy, doing it all alone,
          and the mindset spirals that creep in when results feel slow.
        </p>
      </Reveal>
      <Reveal delay={0.18}>
        <blockquote className="mt-16 border-t border-ivory/15 pt-12 max-w-3xl mx-auto">
          <p className="font-serif italic text-2xl md:text-3xl lg:text-[2.4rem] leading-snug text-ivory">
            "Consistent clients aren't built from one great post. They're built from{" "}
            <span className="not-italic underline decoration-mocha decoration-2 underline-offset-[6px]">
              thirty real conversations.
            </span>"
          </p>
          <footer className="mt-6 font-sans text-xs uppercase tracking-[0.32em] text-mocha">
            — Lydia
          </footer>
        </blockquote>
      </Reveal>
    </div>
  </section>
);

const LeadMagnet = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name || null,
        email,
        source: "lead_magnet_consistent_clients_guide",
      });
      setSubmitted(true);
      toast.success("Your guide is on the way. Opening it now…");
      // Open the PDF immediately as the in-page delivery
      window.open(LEAD_MAGNET_PDF, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-cream" data-testid="lead-magnet">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-28 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Left: PDF cover mockup */}
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative mx-auto max-w-sm">
              {/* Soft shadow plate */}
              <div className="absolute -inset-4 bg-espresso/5 translate-x-3 translate-y-3" aria-hidden />
              {/* Cover */}
              <a
                href={LEAD_MAGNET_PDF}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block bg-espresso text-ivory aspect-[3/4] p-8 md:p-10 hover:bg-mocha transition-colors duration-500 shadow-xl"
                data-testid="lead-magnet-cover"
              >
                <div className="flex flex-col h-full">
                  <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha">
                    A free guide
                  </p>
                  <div className="mt-6 w-10 h-px bg-mocha" />
                  <h3 className="mt-8 font-serif text-3xl md:text-4xl leading-[1.1] text-ivory">
                    The Consistent <em className="italic">Clients</em> Guide
                  </h3>
                  <p className="mt-6 font-serif italic text-lg text-ivory/75 leading-snug">
                    The exact framework I use with private clients to move from inconsistent
                    months to steady £5k–£10k.
                  </p>
                  <div className="mt-auto pt-8 flex items-center justify-between border-t border-ivory/15">
                    <img
                      src={LOGO_URL}
                      alt="Lydia Kimmerling"
                      className="h-8 w-auto"
                      style={{ mixBlendMode: "screen" }}
                    />
                    <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-mocha">
                      Faith Over Force
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </Reveal>

          {/* Right: copy + form */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal>
              <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
                Free guide · Instant download
              </p>
              <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.4rem] leading-[1.05] text-espresso">
                Not ready for the call? <em className="italic">Start here.</em>
              </h2>
              <p className="mt-6 font-sans text-base md:text-lg text-espresso/80 leading-relaxed max-w-xl">
                Download <em>The Consistent Clients Guide</em> and learn the exact thing my
                private clients do to land steady, well-paid clients without burning out, posting
                more, or chasing strangers in DMs.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <ul className="mt-8 space-y-3 font-sans text-base text-espresso/85 max-w-xl">
                <li className="flex gap-3"><Check className="w-5 h-5 text-sage shrink-0 mt-0.5" strokeWidth={1.6} /><span>The "30 real conversations" strategy in full</span></li>
                <li className="flex gap-3"><Check className="w-5 h-5 text-sage shrink-0 mt-0.5" strokeWidth={1.6} /><span>How to position yourself so the right women say yes</span></li>
                <li className="flex gap-3"><Check className="w-5 h-5 text-sage shrink-0 mt-0.5" strokeWidth={1.6} /><span>What to do when results feel slow (and you want to quit)</span></li>
              </ul>
            </Reveal>

            <Reveal delay={0.18}>
              {!submitted ? (
                <form
                  onSubmit={submit}
                  data-testid="lead-magnet-form"
                  className="mt-10 max-w-xl"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="First name"
                      data-testid="lead-magnet-name"
                      className="bg-transparent border-b border-espresso/25 focus:border-espresso text-espresso placeholder:text-espresso/45 py-3 px-1 outline-none font-sans text-base transition-colors"
                    />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      data-testid="lead-magnet-email"
                      className="bg-transparent border-b border-espresso/25 focus:border-espresso text-espresso placeholder:text-espresso/45 py-3 px-1 outline-none font-sans text-base transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-magnet-submit"
                    className="btn-primary mt-7 w-full sm:w-auto disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Send Me The Guide"}
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <p className="mt-4 font-sans text-xs text-espresso/55">
                    No spam. Unsubscribe anytime. You'll also get occasional notes from me.
                  </p>
                </form>
              ) : (
                <div className="mt-10 max-w-xl bg-ivory border border-espresso/15 p-6">
                  <p className="font-serif italic text-lg text-espresso">
                    The guide is opening in a new tab. A copy has also been logged for your
                    inbox.
                  </p>
                  <a
                    href={LEAD_MAGNET_PDF}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline mt-5"
                    data-testid="lead-magnet-download"
                  >
                    Open the guide again
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Programme = () => (
  <section id="programme" className="relative bg-ivory">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
            What is Faith Over Force?
          </p>
          <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
            A grounded way to build a business that <em className="italic">works.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-7 lg:pt-4">
          <p className="font-sans text-base md:text-lg text-espresso/80 leading-relaxed">
            Faith Over Force isn't about sitting back and hoping clients arrive. It's about
            grounded strategy, consistent action and the kind of trust that stops you spiralling
            or burning out when results feel slow.
          </p>
          <p className="mt-5 font-sans text-base md:text-lg text-espresso/80 leading-relaxed">
            This isn't manifestation, and it isn't hustle culture. It's <em>strategic, practical business
            mentoring</em> with deep coaching support, built for women who want real numbers,
            real conversations and real momentum.
          </p>
        </Reveal>
      </div>

      <div className="mt-20 grid md:grid-cols-2 gap-6 md:gap-8" data-testid="pillars-grid">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <Reveal key={p.title} delay={i * 0.08}>
              <article className="group relative bg-cream border border-espresso/10 p-8 md:p-12 h-full transition-colors duration-500 hover:bg-espresso hover:text-ivory">
                <div className="flex items-center gap-4 mb-6">
                  <span className="w-12 h-12 bg-sage-deep flex items-center justify-center text-cream group-hover:bg-cream group-hover:text-sage-deep transition-colors">
                    <Icon className="w-6 h-6" strokeWidth={1.4} />
                  </span>
                  <span className="font-sans text-xs uppercase tracking-[0.32em] text-sage-deep group-hover:text-ivory/70 transition-colors">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-espresso group-hover:text-ivory transition-colors">
                  {p.title}
                </h3>
                <p className="mt-5 font-sans text-base text-espresso/75 group-hover:text-ivory/80 leading-relaxed transition-colors">
                  {p.body}
                </p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const Included = () => (
  <section id="included" className="bg-cream">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <div className="grid lg:grid-cols-12 gap-12 items-end">
        <Reveal className="lg:col-span-7">
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
            What's Included
          </p>
          <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
            Everything you need. <em className="italic">Nothing you don't.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-5">
          <p className="font-sans text-base md:text-lg text-espresso/75 leading-relaxed">
            Built around the eight things that actually move the needle when you're growing a
            service-based business in real life. Not in theory.
          </p>
        </Reveal>
      </div>

      <ul
        className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-espresso/10"
        data-testid="included-list"
      >
        {INCLUDED.map((item, i) => {
          const Icon = item.icon;
          return (
            <Reveal key={item.title} delay={(i % 4) * 0.06}>
              <li className="bg-cream p-8 h-full flex flex-col justify-between min-h-[220px] hover:bg-ivory transition-colors">
                <Icon className="w-7 h-7 text-sage" strokeWidth={1.4} />
                <div className="mt-10">
                  <span className="font-sans text-[10px] uppercase tracking-[0.32em] text-sage-deep">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-serif text-2xl text-espresso leading-tight">
                    {item.title}
                  </h3>
                  <p className="mt-2 font-sans text-sm text-espresso/65">{item.note}</p>
                </div>
              </li>
            </Reveal>
          );
        })}
      </ul>
    </div>
  </section>
);

const Investment = () => (
  <section className="bg-ivory">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <Reveal className="text-center max-w-3xl mx-auto">
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
          The Investment
        </p>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
          Pay once. Stay for a year. <em className="italic">Then stay as long as you need.</em>
        </h2>
        <p className="mt-8 font-sans text-base md:text-lg text-espresso/75 leading-relaxed">
          Most coaches charge £3,000 for three months, then you're on your own. Faith Over Force
          is a <strong className="font-semibold">minimum 12-month container</strong> with
          bi-weekly group mentoring for as long as you stay. Whether that's one year or three.
        </p>
      </Reveal>

      {/* Pricing cards */}
      <Reveal delay={0.1}>
        <div
          className="mt-14 grid md:grid-cols-2 gap-5 md:gap-6"
          data-testid="investment-card"
        >
          {/* Card 1 — Payment Plan (lower headline first) */}
          <article className="bg-cream border border-espresso/15 p-10 md:p-12 flex flex-col">
            <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha">
              Payment Plan
            </p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-[4.5rem] md:text-[5.5rem] text-espresso leading-none">
                £333
              </span>
              <span className="font-sans text-sm text-espresso/70 uppercase tracking-wider">
                / month
              </span>
            </div>
            <p className="mt-3 font-sans text-sm text-espresso/70">
              £250 deposit, then 9 monthly payments of £333
            </p>
            <ul className="mt-8 space-y-3 font-sans text-sm md:text-base text-espresso/85 flex-1">
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>Spread the investment across the year</span>
              </li>
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>Same access, same support, same results</span>
              </li>
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>£3,247 total over the year</span>
              </li>
            </ul>
            <a href={BOOKING_LINK} className="btn-outline mt-10 w-full" data-testid="investment-cta-plan" target="_blank" rel="noopener noreferrer">
              Book a Consultation
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </article>

          {/* Card 2 — Pay in Full */}
          <article className="relative bg-espresso text-ivory p-10 md:p-12 flex flex-col">
            <div className="absolute -top-3 left-10 bg-mocha text-ivory font-sans text-[10px] uppercase tracking-[0.32em] px-4 py-1.5">
              Best Value
            </div>
            <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha">
              Pay in Full
            </p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-[4.5rem] md:text-[5.5rem] text-ivory leading-none">
                £2,500
              </span>
              <span className="font-serif text-xl text-ivory/60 line-through">£3,247</span>
            </div>
            <p className="mt-3 font-sans text-sm text-ivory/70">
              £250 deposit + one payment of £2,250
            </p>
            <ul className="mt-8 space-y-3 font-sans text-sm md:text-base text-ivory/90 flex-1">
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>Save £747</span>
              </li>
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>One commitment, one payment</span>
              </li>
              <li className="flex gap-3">
                <Check className="w-5 h-5 text-mocha shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>Full programme access from day one</span>
              </li>
            </ul>
            <a href={BOOKING_LINK} className="btn-primary btn-light mt-10 w-full" data-testid="investment-cta-paid" target="_blank" rel="noopener noreferrer">
              Book a Consultation
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </article>
        </div>
      </Reveal>

      {/* Value timeline — 90 days / 12 months / lifetime */}
      <Reveal delay={0.15}>
        <div className="mt-20 md:mt-24">
          <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha mb-6 text-center">
            What you're actually buying
          </p>
          <div className="grid md:grid-cols-3 gap-px bg-espresso/15">
            <div className="bg-ivory p-8 md:p-10">
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-mocha">
                Within
              </p>
              <p className="mt-2 font-serif text-5xl md:text-6xl text-espresso leading-none">
                90<span className="text-3xl md:text-4xl ml-1 text-espresso/60">days</span>
              </p>
              <p className="mt-4 font-sans text-sm md:text-base text-espresso/80 leading-relaxed">
                First steady clients land. Real conversations, real money in, real momentum.
              </p>
            </div>
            <div className="bg-ivory p-8 md:p-10">
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-mocha">
                Minimum
              </p>
              <p className="mt-2 font-serif text-5xl md:text-6xl text-espresso leading-none">
                12<span className="text-3xl md:text-4xl ml-1 text-espresso/60">months</span>
              </p>
              <p className="mt-4 font-sans text-sm md:text-base text-espresso/80 leading-relaxed">
                Held twice a week, every week. Around £210/month at pay-in-full,
                less than one new client a month.
              </p>
            </div>
            <div className="bg-ivory p-8 md:p-10">
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-mocha">
                Then
              </p>
              <p className="mt-2 font-serif text-5xl md:text-6xl text-espresso leading-none italic">
                Stay
              </p>
              <p className="mt-4 font-sans text-sm md:text-base text-espresso/80 leading-relaxed">
                Bi-weekly group mentoring stays open as long as you need. No new fee. No upsell.
                Three years if that's what it takes.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Personal note */}
      <Reveal delay={0.2}>
        <div className="mt-20 md:mt-24 max-w-3xl mx-auto text-center">
          <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha mb-5">
            A note from Lydia
          </p>
          <p className="font-serif italic text-xl md:text-2xl lg:text-[1.6rem] text-espresso/90 leading-snug">
            "I've been burnt by expensive coaches myself. I built this because I believe you
            should get the support you actually need, not a watered-down version of it. You
            shouldn't have to start over with someone new every 90 days."
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="bg-cream">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <Reveal className="lg:col-span-7">
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
          About
        </p>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
          A little about <em className="italic">Lydia.</em>
        </h2>
        <div className="mt-10 space-y-6 font-sans text-base md:text-lg text-espresso/80 leading-relaxed">
          <p>
            Lydia Kimmerling is a master life coach and business mentor with{" "}
            <strong className="font-semibold">16 years' experience</strong>. A TEDx speaker, she's
            been featured on BBC, Happy Place Festival, Hello! Magazine, The Times, Marie Claire,
            Metro and Ferne McCann's TV show.
          </p>
          <p>
            She burnt out in 2022, rebuilt from scratch with clarity and integrity, and created
            Faith Over Force for women who are done with guessing and ready to build something
            <em> steady.</em>
          </p>
          <p>
            Warm, bold, deeply experienced and genuinely invested in the women she works with.
          </p>
        </div>
        <div className="mt-10 flex flex-wrap gap-8 text-espresso">
          <Stat number="16" label="Years of experience" />
          <Stat number="1,000s" label="Women supported" />
          <Stat number="100s" label="Businesses launched" />
        </div>
      </Reveal>

      <Reveal delay={0.1} className="lg:col-span-5">
        <div className="relative">
          <img
            src={ABOUT_PORTRAIT}
            alt="Lydia Kimmerling"
            data-testid="about-portrait"
            className="relative w-full aspect-square object-cover"
            style={{ objectPosition: "center" }}
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const Stat = ({ number, label }) => (
  <div>
    <div className="font-serif text-4xl md:text-5xl text-espresso leading-none">{number}</div>
    <div className="mt-2 font-sans text-[11px] uppercase tracking-[0.28em] text-mocha">
      {label}
    </div>
  </div>
);

const Evidence = () => (
  <section className="bg-ivory border-t border-espresso/10" data-testid="evidence-strip">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-12 md:mb-14">
        <Reveal className="lg:col-span-7">
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-4">
            The proof
          </p>
          <h2 className="font-serif text-[2.2rem] md:text-4xl lg:text-[3rem] leading-[1.05] text-espresso">
            You can't cheat <em className="italic">experience.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.08} className="lg:col-span-5">
          <p className="font-sans text-base text-espresso/70 leading-relaxed">
            Sixteen years in. Not chasing trends, not riding the AI wave, not promising overnight
            wins. Just real work that keeps women in business when the world inevitably moves on.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {EVIDENCE.map((item, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <figure className="group relative overflow-hidden">
              <img
                src={item.src}
                alt={item.caption}
                loading="lazy"
                className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <figcaption className="absolute inset-0 flex flex-col justify-end p-4 md:p-5 bg-gradient-to-t from-espresso/85 via-espresso/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <p className="font-serif text-lg md:text-xl text-ivory leading-tight">
                  {item.caption}
                </p>
                <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.28em] text-ivory/80">
                  {item.sub}
                </p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section id="testimonials" className="relative bg-mocha text-ivory grain">
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <Reveal>
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-ivory/70 mb-5">
          Words from the women
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] max-w-3xl">
          What it actually feels like on the <em className="italic">other side.</em>
        </h2>
      </Reveal>

      {/* Notable clients moved to its own section earlier in the page */}

      <div className="mt-16 md:mt-20 grid md:grid-cols-2 gap-6 md:gap-8" data-testid="testimonials-grid">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.07}>
            <figure className="bg-ivory text-espresso p-8 md:p-12 h-full flex flex-col justify-between">
              <span className="font-serif text-5xl text-mocha leading-none">"</span>
              <blockquote className="mt-2 font-serif text-xl md:text-2xl leading-snug text-espresso">
                {t.quote}
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-espresso/15 font-sans text-xs uppercase tracking-[0.32em] text-mocha">
                — {t.name}
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Fit = () => (
  <section className="bg-ivory">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
      <Reveal className="text-center max-w-3xl mx-auto">
        <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-5">
          Is it right for you?
        </p>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
          Let's be honest about <em className="italic">the fit.</em>
        </h2>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 gap-px bg-espresso/15" data-testid="fit-grid">
        <Reveal>
          <div className="bg-cream p-10 md:p-14 h-full">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-10 h-10 rounded-full bg-espresso flex items-center justify-center">
                <Check className="w-5 h-5 text-ivory" strokeWidth={2} />
              </span>
              <h3 className="font-serif text-3xl text-espresso">A great fit if…</h3>
            </div>
            <ul className="space-y-5">
              {FIT_YES.map((f, i) => (
                <li key={i} className="flex gap-3 font-sans text-base md:text-lg text-espresso/85 leading-relaxed">
                  <Check className="w-5 h-5 text-mocha shrink-0 mt-1" strokeWidth={1.6} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="bg-ivory p-10 md:p-14 h-full">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-10 h-10 rounded-full border border-espresso flex items-center justify-center">
                <X className="w-5 h-5 text-espresso" strokeWidth={2} />
              </span>
              <h3 className="font-serif text-3xl text-espresso">Not a fit if…</h3>
            </div>
            <ul className="space-y-5">
              {FIT_NO.map((f, i) => (
                <li key={i} className="flex gap-3 font-sans text-base md:text-lg text-espresso/70 leading-relaxed">
                  <X className="w-5 h-5 text-espresso/50 shrink-0 mt-1" strokeWidth={1.6} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const FinalCTA = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    // Open the PDF in a new tab IMMEDIATELY (synchronously inside the click handler)
    // so the browser treats it as a user-initiated popup and doesn't redirect the current page.
    const pdfTab = window.open(LEAD_MAGNET_PDF, "_blank", "noopener,noreferrer");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name || null,
        email,
        source: "lead_magnet_consistent_clients_guide",
      });
      setSubmitted(true);
      toast.success(
        pdfTab
          ? "Your guide is opening in a new tab. Welcome in."
          : "Your guide is ready — tap below to open it."
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-espresso text-ivory">
      <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-36 lg:py-44">
        <Reveal>
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-mocha mb-6 text-center">
            One last thing
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-[2.6rem] md:text-5xl lg:text-[4.2rem] leading-[1.05] text-center">
            You don't have to figure this out <em className="italic">alone.</em>
          </h2>
        </Reveal>

        {/* PDF lead magnet */}
        <Reveal delay={0.12}>
          <div className="mt-14 max-w-3xl mx-auto bg-ivory/5 border border-ivory/15 p-6 md:p-10">
            <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
              <a
                href={LEAD_MAGNET_PDF}
                target="_blank"
                rel="noopener noreferrer"
                className="md:col-span-5 relative block group max-w-xs md:max-w-none mx-auto md:mx-0 w-full"
                data-testid="lead-magnet-cover"
              >
                {/* Shadow plate behind */}
                <div className="absolute -inset-1.5 bg-sage-deep/30 translate-x-2 translate-y-2 transition-transform duration-500 group-hover:translate-x-1 group-hover:translate-y-1" aria-hidden />

                {/* The "book cover" itself */}
                <div className="relative bg-cream text-espresso aspect-[3/4] overflow-hidden transition-shadow duration-500 group-hover:shadow-2xl">
                  {/* Grain texture overlay */}
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-multiply"
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/%3E%3C/svg%3E\")",
                    }}
                  />

                  {/* Decorative top border */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-espresso" />
                  {/* Decorative side line */}
                  <div className="absolute top-6 bottom-6 left-3 w-px bg-espresso/15" />

                  <div className="relative h-full flex flex-col p-5 md:p-6 pl-7 md:pl-8">
                    {/* Top: vol + free */}
                    <div className="flex items-start justify-between">
                      <p className="font-sans text-[8px] md:text-[9px] uppercase tracking-[0.36em] text-espresso/65">
                        Vol. 01
                      </p>
                      <p className="font-sans text-[8px] md:text-[9px] uppercase tracking-[0.36em] text-sage-deep">
                        Free
                      </p>
                    </div>

                    {/* Decorative ornament */}
                    <div className="mt-4 flex items-center gap-2">
                      <span className="h-px w-4 bg-sage-deep" />
                      <span className="font-serif text-sage-deep text-base leading-none">✦</span>
                      <span className="h-px flex-1 bg-sage-deep/30" />
                    </div>

                    {/* Title */}
                    <h3 className="mt-5 font-serif text-[1.6rem] md:text-[1.5rem] lg:text-[1.75rem] leading-[1.02] text-espresso tracking-tight">
                      The Consistent <em className="italic text-sage-deep block">Clients</em> Guide
                    </h3>

                    {/* Italic descriptor */}
                    <p className="mt-3 font-serif italic text-[10px] md:text-[10.5px] lg:text-xs text-espresso/70 leading-snug max-w-[12rem]">
                      The framework my private clients use to stop hoping and start booking.
                    </p>

                    {/* Mini contents teaser */}
                    <ul className="mt-auto mb-2 space-y-1 font-sans text-[7.5px] md:text-[8px] lg:text-[9px] uppercase tracking-[0.18em] text-espresso/55">
                      <li>· Positioning</li>
                      <li>· The 30 Conversations Method</li>
                      <li>· Mindset when results feel slow</li>
                    </ul>

                    {/* Bottom: signature stamp */}
                    <div className="pt-2 border-t border-espresso/15 flex items-end justify-between">
                      <div>
                        <p className="font-sans text-[7px] uppercase tracking-[0.32em] text-espresso/50">
                          By
                        </p>
                        <img
                          src={LOGO_URL}
                          alt="Lydia Kimmerling"
                          className="h-4 md:h-4 lg:h-5 w-auto mt-0.5"
                          style={{ filter: "invert(1)", mixBlendMode: "multiply" }}
                        />
                      </div>
                      <p className="font-sans text-[7px] uppercase tracking-[0.32em] text-espresso/50 text-right leading-tight">
                        Faith<br />Over Force
                      </p>
                    </div>
                  </div>
                </div>

                {/* Corner ribbon */}
                <div className="absolute -top-1 -right-1 bg-sage-deep text-ivory font-sans text-[8px] md:text-[9px] uppercase tracking-[0.28em] px-3 py-1.5 z-10">
                  Free
                </div>
              </a>

              <div className="md:col-span-7">
                {!submitted ? (
                  <form onSubmit={submit} data-testid="lead-form">
                    <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-mocha mb-2">
                      Free guide · Instant download
                    </p>
                    <p className="font-serif text-xl md:text-2xl text-ivory leading-snug">
                      Not ready for the call? <em className="italic">Start here.</em>
                    </p>
                    <p className="mt-3 font-sans text-sm text-ivory/70 leading-relaxed">
                      Download <em>The Consistent Clients Guide</em> — the exact thing my
                      private clients do to land steady, well-paid clients.
                    </p>
                    <div className="mt-6 grid sm:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="First name"
                        data-testid="lead-form-name"
                        className="bg-transparent border-b border-ivory/30 focus:border-ivory text-ivory placeholder:text-ivory/50 py-3 px-1 outline-none font-sans text-base transition-colors"
                      />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Email address"
                        data-testid="lead-form-email"
                        className="bg-transparent border-b border-ivory/30 focus:border-ivory text-ivory placeholder:text-ivory/50 py-3 px-1 outline-none font-sans text-base transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="lead-form-submit"
                      className="btn-primary btn-light mt-6 w-full sm:w-auto disabled:opacity-60"
                    >
                      {loading ? "Sending…" : "Send Me The Guide"}
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <p className="mt-4 font-sans text-xs text-ivory/50">
                      No spam. Unsubscribe anytime. You'll also get the occasional note from me.
                    </p>
                  </form>
                ) : (
                  <div>
                    <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-mocha mb-3">
                      Thank you
                    </p>
                    <p className="font-serif italic text-xl md:text-2xl text-ivory leading-snug">
                      Your guide is opening in a new tab.
                    </p>
                    <a
                      href={LEAD_MAGNET_PDF}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary btn-light mt-6"
                      data-testid="lead-form-reopen"
                    >
                      Open the guide again
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-6 md:gap-8">
          <Reveal>
            <a
              href={`${INSTAGRAM_URL}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-instagram-btn"
              className="group block border border-ivory/20 p-8 md:p-10 hover:bg-ivory hover:text-espresso transition-colors duration-500"
            >
              <Instagram className="w-7 h-7 mb-6" strokeWidth={1.4} />
              <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-mocha group-hover:text-mocha">
                On Instagram
              </p>
              <p className="mt-3 font-serif text-2xl md:text-3xl">
                Message <em className="italic">CLIENTS</em> to {INSTAGRAM_HANDLE}
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.28em]">
                Open Instagram <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.08}>
            <a
              href={BOOKING_LINK}
              data-testid="final-book-call-btn"
              className="group block border border-ivory/20 p-8 md:p-10 hover:bg-ivory hover:text-espresso transition-colors duration-500"
              target="_blank"
              rel="noopener noreferrer"
            >
              <CalendarCheck className="w-7 h-7 mb-6" strokeWidth={1.4} />
              <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-mocha">
                Or book a call
              </p>
              <p className="mt-3 font-serif text-2xl md:text-3xl">
                A free <em className="italic">Consistent Clients</em> Call
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.28em]">
                Reserve Your Time <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </span>
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <p className="mt-20 text-center font-serif italic text-2xl md:text-3xl text-ivory/90 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
            <span>With love,</span>
            <img
              src={LOGO_URL}
              alt="Lydia Kimmerling"
              className="h-14 md:h-16 w-auto inline-block"
              style={{ mixBlendMode: "screen" }}
            />
            <span className="text-mocha">†</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const NotableClients = () => {
  if (!NOTABLE.length) return null;

  return (
    <section className="bg-cream" data-testid="notable-clients">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-24 lg:py-28">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {NOTABLE.map((n, i) => (
            <Reveal key={n.name} delay={i * 0.08}>
              <article className="bg-ivory text-espresso h-full flex flex-col">
                <img
                  src={n.image}
                  alt={n.name}
                  className="w-full aspect-[5/4] object-cover"
                  style={{ objectPosition: n.imagePos || "center" }}
                  loading="lazy"
                />
                <div className="p-7 md:p-9 flex-1 flex flex-col">
                  <span className="font-serif text-4xl text-mocha leading-none">"</span>
                  <blockquote className="mt-1 font-serif text-lg md:text-xl leading-snug text-espresso flex-1">
                    {n.quote}
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-espresso/15">
                    <p className="font-serif text-xl md:text-2xl text-espresso">{n.name}</p>
                    <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.32em] text-mocha">
                      {n.title}
                    </p>
                  </figcaption>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-espresso text-ivory/60 border-t border-ivory/10">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 flex flex-col md:flex-row gap-6 items-center justify-between">
      <div className="text-center md:text-left">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
          <img
            src={LOGO_URL}
            alt="Lydia Kimmerling"
            className="h-10 md:h-12 w-auto mx-auto md:mx-0"
            style={{ mixBlendMode: "screen" }}
          />
          <span className="hidden md:inline text-mocha">·</span>
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-ivory/70">
            Faith Over Force
          </p>
        </div>
        <p className="mt-3 font-serif italic text-[11px] text-ivory/25 leading-snug max-w-md">
          "Come to me, all you who are weary and burdened, and I will give you rest." — Matthew 11:28
        </p>
      </div>
      <p className="font-sans text-xs uppercase tracking-[0.28em]">
        © {new Date().getFullYear()} All rights reserved
      </p>
    </div>
  </footer>
);

const Landing = () => {
  useEffect(() => {
    document.title = "Faith Over Force | Lydia Kimmerling";
  }, []);

  return (
    <main className="bg-ivory text-espresso">
      <Toaster position="top-center" richColors closeButton />
      <Nav />
      <Hero />
      <PainPoints />
      <RealProblem />
      <Programme />
      <Included />
      <NotableClients />
      <Investment />
      <About />
      <Evidence />
      <Testimonials />
      <Fit />
      <FinalCTA />
      <Footer />
    </main>
  );
};

export default Landing;
